import { creatorbase, creatorbaseQueries } from '@round/api';
import { GenericDropdownOption } from 'App.types';
import useSelectProps from 'Hooks/useSelectProps';
import { useInfiniteQuery } from '@tanstack/react-query';
import { SelectProps } from '@round/ui-kit';
import { usePartnership } from '@round/creatorbase-helpers/api-hooks';
import { isNumber } from 'utility/utility';
import { useMemo } from 'react';

const queryOptions = creatorbaseQueries.partnerships.options.infinite;

export type PartnershipOption = GenericDropdownOption<number>;

export const mapPartnershipToOption = (partnership: creatorbase.Partnership): PartnershipOption => ({
    value: partnership.id,
    label: partnership.agency_name,
});

type Params = {
    initOn: 'menuOpen' | 'mount';
    initialValueId?: number;
    params?: Pick<creatorbase.GetPartnershipsParams, 'client_id' | 'state'>;
    enabled?: boolean;
};

type Return = {
    getProps: (override?: Pick<SelectProps<PartnershipOption>, 'onChange'>) => SelectProps<PartnershipOption>;
};

export default function usePartnershipSelect({ enabled = true, ...params }: Params = { initOn: 'mount' }): Return {
    const { data: partnership } = usePartnership(params.initialValueId, {
        enabled: enabled && isNumber(params.initialValueId),
    });
    const initialValue = useMemo(() => (partnership ? mapPartnershipToOption(partnership) : null), [partnership]);

    const { getProps, isMenuOpen } = useSelectProps<PartnershipOption, false>({ initialValue });

    const { data, isFetching, hasNextPage, fetchNextPage, isError } = useInfiniteQuery({
        ...queryOptions({ ...params.params, page_size: 25 }),
        enabled: enabled && ((params.initOn === 'menuOpen' && isMenuOpen) || params.initOn === 'mount'),
    });

    const options = data?.pages.flatMap((page) => page.results.map(mapPartnershipToOption)) ?? [];

    return {
        getProps: (override) =>
            getProps({
                ...override,
                options,
                noOptionsMessage: () => (isError ? 'Could not get agencies' : 'No agencies found'),
                isLoading: isFetching,
                onMenuScrollToBottom: () => {
                    if (hasNextPage && !isFetching) {
                        fetchNextPage();
                    }
                },
            }),
    };
}
